/**
 * What the user sees when the server refuses an action for lack of permission -- ONE message, ONE mechanism for the whole app.
 *
 * The backend answers 403 with a technical text ("Missing permission: tasks.create"). lib/authenticated-fetch.ts passes every API answer through
 * `softenPermissionDenied`: a refused WRITE (create / update / delete) keeps its 403 status but carries the generic message, so any screen that already shows the
 * server's error shows this text; and it announces the refusal on a small bus that the open form (styles/GenericForm.tsx) listens to, so a form that does not
 * wire the error through still shows the warning in its usual place. The technical text is kept in the console for debugging, never shown.
 */

export const INSUFFICIENT_PERMISSIONS_MESSAGE = "You don't have sufficient permissions to perform this action.";
export const PERMISSION_DENIED_CODE = 'insufficient_permissions';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// POST routes that only READ (a list's export / KPI summary / search): a refusal there is not a failed form submit
const READ_ONLY_POST = /\/(export|summary|search)(\?|$)/;

/** Is this a request that changes something (and so may come from a form's Save / Delete)? */
export function isWriteRequest(method: string | undefined, url: string): boolean {
  return WRITE_METHODS.has(String(method || 'GET').toUpperCase()) && !READ_ONLY_POST.test(url);
}

// ---- the bus: the OPEN form that is on top hears the refusal ------------------------------------------------------------------------------------------------
type Listener = () => void;
const openForms: Array<{ id: symbol; listener: Listener }> = [];

/** An open form registers while it is open; only the most recently opened one is told (the one the user is looking at). Returns the unregister function. */
export function registerOpenForm(listener: Listener): () => void {
  const id = Symbol('open-form');
  openForms.push({ id, listener });
  return () => {
    const at = openForms.findIndex((entry) => entry.id === id);
    if (at >= 0) openForms.splice(at, 1);
  };
}

export function announcePermissionDenied(): void {
  const top = openForms[openForms.length - 1];
  if (top) top.listener();
}

/**
 * A 403 on a write becomes the generic message (same status, same JSON shape, `code: 'insufficient_permissions'`) and is announced to the open form.
 * Any other answer is returned untouched.
 */
export async function softenPermissionDenied(response: Response, method: string | undefined, url: string): Promise<Response> {
  if (response.status !== 403 || !isWriteRequest(method, url)) return response;
  let technical: unknown = null;
  try {
    technical = await response.clone().json();
  } catch {
    technical = null;
  }
  // for debugging only; never shown to the user
  console.warn('[permissions] request refused (403)', method, url, technical);
  announcePermissionDenied();
  const body = {
    ...(technical && typeof technical === 'object' ? (technical as Record<string, unknown>) : {}),
    success: false,
    error: INSUFFICIENT_PERMISSIONS_MESSAGE,
    message: INSUFFICIENT_PERMISSIONS_MESSAGE,
    code: PERMISSION_DENIED_CODE,
  };
  const headers = new Headers(response.headers);
  headers.set('Content-Type', 'application/json');
  headers.delete('Content-Length');
  return new Response(JSON.stringify(body), { status: 403, statusText: response.statusText, headers });
}

/**
 * A short, safe reason for one failed request, for lists of results ("Deleted 2. 1 failed: ..."): the generic message for a permission refusal, the server's own
 * hand-written text for a rejected request (400 / 404 / 409 / 422), and a neutral sentence for anything else -- never an internal server detail.
 */
export function failureReason(status: number, body: { error?: unknown; code?: unknown } | null | undefined): string {
  if (status === 403 || body?.code === PERMISSION_DENIED_CODE) return INSUFFICIENT_PERMISSIONS_MESSAGE;
  if (status === 401) return 'Your session has expired. Please log in again.';
  if (status === 404) return 'It no longer exists.';
  if ([400, 409, 422].includes(status) && typeof body?.error === 'string' && body.error.trim()) return body.error.trim();
  return 'Something went wrong on our side. Please try again.';
}
